import { AudioError } from '@/state/presenters';
import { getDurationFromArrayBuffer } from '@/utils/get-duration-array-buffer';
import { WordLevelTimestamp } from '@/utils/types';
import { isWordLevelTimestampValid } from '@/utils/utils';
import { escapeSsmlText } from '@d-id/ssml';
import { IVoice, Providers } from '@d-id/types';
import { useCallback, useState } from 'react';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { useUploadActivePageTtsCache } from '../pages/AdvancedEditor/hooks/useUploadTtsCache';
import useTtsData from '../services/tts/googleApiKeyManager';
import {
    AUDIO_ELEMENT_IDENTIFIER,
    GOOGLE_TTS_API_PREFIX,
    GOOGLE_TTS_REQUEST_CONTENT_TYPE,
    GOOGLE_VOICE_ID_SEPERATORS_CHARACTER,
    MP3_ENCODING_IDENTIFIER,
    MPEG_SOURCE_BUFFER_TYPE_IDENTIFIER,
    POST_REQUEST_IDENTIFIER,
    SOURCE_OPEN_EVENT_IDENTIFIER,
    SPEAK_CLOSE_TAG_WRAPPER,
    SPEAK_TAG_WRAPPER,
    UPDATEEND_EVENT_IDENTIFIER,
} from './constants';
import { activePlayerIdAtom, PlayerMode, playerModeAtom } from './useAudioPlayer';

type GoogleTimePoints = {
    markName: string;
    timeSeconds: number;
};

function addMarksToText(text: string): string {
    const ssmlTagRegex = /<[^>]+>/;
    const markedText = text
        .split(/(\s+|<[^>]+>)/) // Split by spaces and SSML tags, no need to mark those
        .filter(part => part.trim() !== '')
        .reduce((acc, word, index) => {
            if (ssmlTagRegex.test(word)) {
                acc += `${word} `;
            } else {
                acc += `<mark name="word${index}"/>${word} `;
            }
            return acc;
        }, '')
        .trim();

    return `<speak>${markedText}</speak>`;
}

function mapWordTimestamps(timepoints: GoogleTimePoints[], text: string): WordLevelTimestamp[] {
    if (!timepoints?.length) {
        return [];
    }

    const sanitizedTextWithoutSsmlTags = text.replace(/<[^>]+>/g, '').trim();
    const words = sanitizedTextWithoutSsmlTags.split(/\s+/);

    return timepoints.map((tp, index) => {
        const word = words[index] || '';
        const startTime = tp.timeSeconds ?? 0;
        const endTime = index < timepoints.length - 1 ? (timepoints[index + 1].timeSeconds ?? 0) : startTime;

        return {
            startTime,
            endTime,
            word,
        };
    });
}

async function getReader(voiceId: string, text: string, signal: AbortSignal, googleToken: string, setError?: (error: any) => void) {
    const voiceIdParts = voiceId.split(GOOGLE_VOICE_ID_SEPERATORS_CHARACTER);
    const languageCode = `${voiceIdParts[0]}${GOOGLE_VOICE_ID_SEPERATORS_CHARACTER}${voiceIdParts[1]}`;
    const urlFetch = `${GOOGLE_TTS_API_PREFIX}`;

    const markedText = addMarksToText(text);
    const dataRes = {
        input: {
            ssml: `${SPEAK_TAG_WRAPPER} ${markedText} ${SPEAK_CLOSE_TAG_WRAPPER}`,
        },
        voice: {
            languageCode,
            name: voiceId,
        },
        audioConfig: {
            audioEncoding: MP3_ENCODING_IDENTIFIER,
        },
        enableTimePointing: ['SSML_MARK'],
    };
    const otherparam = {
        headers: {
            'content-type': GOOGLE_TTS_REQUEST_CONTENT_TYPE,
            Authorization: `Bearer ${googleToken}`,
        },
        body: JSON.stringify(dataRes),
        method: POST_REQUEST_IDENTIFIER,
        signal,
    };
    let responseJson;
    try {
        const res = await fetch(urlFetch, otherparam);
        responseJson = await res.json();
    } catch (error) {
        setError?.('no-connection');
        return;
    }

    const audioContent = responseJson.audioContent;
    const timepoints = responseJson.timepoints || [];

    return {
        audioContent,
        timepoints,
    };
}

function base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = window.atob(base64);
    const length = binaryString.length;
    const bytes = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

interface Props {
    voice: IVoice | null;
    text: string;
    playerId?: string;
    ttsError?: AudioError | null;
    setTtsError?: (error: AudioError | null) => void;
}

export function useGoogle({ voice, text, playerId = '', ttsError, setTtsError }: Props) {
    const { data } = useTtsData();
    const googleToken = data?.googleToken as string;
    const [playerMode, setPlayerMode] = useRecoilState(playerModeAtom);
    const setActivePlayerId = useSetRecoilState(activePlayerIdAtom);
    const [pause, setPause] = useState<VoidFunction>();
    let wordLevelTimestamps: WordLevelTimestamp[] = [];

    const uploadCachedTts = useUploadActivePageTtsCache();

    const play = useCallback(
        async (voiceid: string, text: string, signal: AbortSignal, volume: number = 1, uploadCache = true) => {
            if (playerMode !== PlayerMode.Ready && playerMode !== PlayerMode.NotLoaded) {
                return;
            }

            setPlayerMode(PlayerMode.Loading);
            setActivePlayerId(playerId);
            const ssmlText = escapeSsmlText(text, true);
            const response = await getReader(voiceid, ssmlText, signal, googleToken, setTtsError);
            if (response) {
                const { audioContent, timepoints } = response;
                wordLevelTimestamps = mapWordTimestamps(timepoints, ssmlText);
                const audio = document.createElement(AUDIO_ELEMENT_IDENTIFIER);
                const mediaSource = new window.MediaSource();
                audio.src = window.URL.createObjectURL(mediaSource);

                audio.onplaying = () => {
                    audio.volume = volume;
                    setPlayerMode(PlayerMode.Playing);
                };

                audio.onended = () => {
                    audio.remove();
                    setPlayerMode(PlayerMode.Ready);
                    setActivePlayerId('');
                };

                const bufferedAudioContent = base64ToArrayBuffer(audioContent);
                mediaSource.addEventListener(SOURCE_OPEN_EVENT_IDENTIFIER, function () {
                    const sourceBuffer = mediaSource.addSourceBuffer(MPEG_SOURCE_BUFFER_TYPE_IDENTIFIER);
                    sourceBuffer.addEventListener(UPDATEEND_EVENT_IDENTIFIER, function () {
                        mediaSource.endOfStream();
                    });
                    sourceBuffer.appendBuffer(bufferedAudioContent);
                });

                mediaSource.onsourceopen = async () => {
                    audio.play();
                };

                audio.src = URL.createObjectURL(mediaSource);
                const duration = await getDurationFromArrayBuffer(bufferedAudioContent);

                if (uploadCache) {
                    if (isWordLevelTimestampValid(wordLevelTimestamps)) {
                        await uploadCachedTts(bufferedAudioContent, text, duration, wordLevelTimestamps);
                    } else {
                        await uploadCachedTts(bufferedAudioContent, text, duration);
                    }
                }
                return audio;
            }
        },
        [uploadCachedTts]
    );

    return {
        playerMode,
        error: ttsError,
        pause,
        wordLevelTimestamps,
        play: async () => {
            setActivePlayerId(playerId);
            const controller = new AbortController();

            if (text && voice?.provider === Providers.Google) {
                const player = await play(voice.id, text, controller.signal);

                setPause(() => () => {
                    setPlayerMode(PlayerMode.Ready);
                    setActivePlayerId('');
                    player?.pause();
                    player?.remove();
                });
            }
        },
        playSilent: async () => {
            const controller = new AbortController();
            if (text && voice?.provider === Providers.Google) {
                await play(voice.id, text, controller.signal, 0);
            }
        },
    };
}
